import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static targets = ["addToCampaignButton", "addToCampaignModal", "allCheckbox",
    "campaignToAddLeadsToSelect", "deleteButton", "exportToCSVButton", "leadCheckbox",
    "listsInsights", "moveToAnotherListButton", "newLeadButton", "createNewCampaignButton",
    "addToCampaignButton", "selectionSize"]

  static values = { leadsCount: Number, leadsListId: Number, outreachEnabled: Boolean }

  connect() {
    // when no leads are shown in the view, no actions are available
    if (this.leadsCountValue === 0) { return }

    var _this = this

    this.allCheckboxTarget.addEventListener("click", function() {
      _this.toggleLeadsCheckboxes()
      _this.toggleListOnlyActionsVisibility()
      _this.toggleSelectionOnlyActionsVisibility()
      _this.toggleListsInsightsVisibility()
    })

    this.leadCheckboxTargets.forEach(function(leadCheckboxTarget) {
      leadCheckboxTarget.addEventListener("click", function() {
        _this.toggleAllCheckbox()
        _this.toggleListOnlyActionsVisibility()
        _this.toggleSelectionOnlyActionsVisibility()
        _this.toggleListsInsightsVisibility()
      })
    })
  }

  // Toggles the checkbox of each lead when the state of the "all" checkbox
  // changes.
  toggleLeadsCheckboxes() {
    var _this = this

    this.leadCheckboxTargets.forEach(function(leadCheckboxTarget) {
      leadCheckboxTarget.checked = _this.allCheckboxTarget.checked
    })
  }

  // Toggles the "all" checkbox when the state of any lead checkbox changes.
  toggleAllCheckbox() {
    if (this.leadsCountValue === this.getSelectedLeads().length) {
      this.allCheckboxTarget.checked = true
    } else {
      this.allCheckboxTarget.checked = false
    }
  }

  // Changes the visibility of actions limited to lists.
  toggleListOnlyActionsVisibility() {
    if(this.getSelectedLeads().length === 0) {
      this.newLeadButtonTarget.style.display = "inline-block"
      this.exportToCSVButtonTarget.parentElement.style.display = "block"
    } else {
      this.newLeadButtonTarget.style.display = "none"
      this.exportToCSVButtonTarget.parentElement.style.display = "none"
    }
  }

  // Changes the visibility of actions limited to selections.
  toggleSelectionOnlyActionsVisibility() {
    if(this.getSelectedLeads().length === 0) {
      this.deleteButtonTarget.parentElement.style.display = "none"

      if(this.hasMoveToAnotherListButtonTarget) {
        this.moveToAnotherListButtonTarget.parentElement.style.display = "none"
      }

      if(this.hasCreateNewCampaignButtonTarget) {
        this.createNewCampaignButtonTarget.parentElement.style.display = "none"
      }

      if(this.hasAddToCampaignButtonTarget) {
        this.addToCampaignButtonTarget.parentElement.style.display = "none"
      }

    } else {
      this.deleteButtonTarget.parentElement.style.display = "block"

      if(this.hasMoveToAnotherListButtonTarget) {
        this.moveToAnotherListButtonTarget.parentElement.style.display = "block"
      }

      if(this.hasCreateNewCampaignButtonTarget) {
        this.createNewCampaignButtonTarget.parentElement.style.display = "block"
      }

      if(this.hasAddToCampaignButtonTarget) {
        this.addToCampaignButtonTarget.parentElement.style.display = "block"
      }
    }
  }

  toggleListsInsightsVisibility() {
    var selectionSize = this.getSelectedLeads().length

    if(selectionSize === 0) {
      this.selectionSizeTarget.style.display = "none"
      this.listsInsightsTarget.style.display = "inline-block"
    } else {
      this.listsInsightsTarget.style.display = "none"

      var selectionSizeText = i18n.t("js.back.lead_actions.count_selected_leads", { count: selectionSize, leads_count: i18n.numberToDelimited(selectionSize, { separator: i18n.translations[i18n.locale].js.number.format.separator, delimiter: i18n.translations[i18n.locale].js.number.format.delimiter}) })

      this.selectionSizeTarget.innerHTML = selectionSizeText
      this.selectionSizeTarget.style.display = "inline-block"
    }
  }

  exportToCSV(event) {
    this.generateForm("post", event.target.dataset.url).submit()
  }

  verify(event) {
    const form = this.generateForm("post", event.target.dataset.url)
    let params = this.getFiltersParams()

    if (this.getSelectedLeads().length > 0) {
      this.addSelectedLeadsToForm(form)
    } else if (params.length === 0 || (params.length === 1 && params[0]["name"] === "leads_list_id")) {
      this.addLeadsListIdToForm(form)
    } else {
      this.addFilteredLeadsToForm(form)
    }

    form.submit()
  }

  moveToAnotherList(event) {
    const form = this.generateForm("post", event.target.dataset.url)

    this.addSelectedLeadsToForm(form)

    form.submit()
  }

  delete(event) {
    const form = this.generateForm("delete", event.target.dataset.url)

    this.addSelectedLeadsToForm(form)

    form.submit()
  }

  // Creates a new campaign, with all the leads matching the filters (referer
  // will be used in the controller) or the selected ones as recipients.
  createNewCampaign(event) {
    if (this.outreachEnabled === false) {
      this.displayGoogleConnectionRequiredFlash()
      return
    }

    if (this.getSelectedLeads().length === 0 && this.getFiltersParams().length === 0) {
      App.displayFlash("danger", i18n.t("js.back.lead_actions.filter_or_select_leads_to_new_campaign"))
      return
    }

    const form = this.generateForm("post", event.target.dataset.url)

    this.addSelectedLeadsToForm(form)

    form.submit()
  }

  // Adds all the leads matching the filters or the selected ones as recipients
  // of an existing campaign.
  addToCampaign(event) {
    if (this.outreachEnabled === false) {
      App.modal($(this.addToCampaignModalTarget), "hide")
      this.displayGoogleConnectionRequiredFlash()
      return
    }

    if (this.getSelectedLeads().length === 0 && this.getFiltersParams().length === 0) {
      App.modal($(this.addToCampaignModalTarget), "hide")
      App.displayFlash("danger", i18n.t("js.back.lead_actions.filter_or_select_leads_to_a_campaign"))
      return
    }

    const form = this.generateForm("put", `/campaigns/${this.campaignToAddLeadsToSelectTarget.value}/audience`)

    if (this.getSelectedLeads().length > 0) {
      this.getSelectedLeads().forEach(function(leadCheckboxTarget) {
        const leadInput = document.createElement("input")
        leadInput.type = "hidden"
        leadInput.name = "campaign[leads_filters][id][]"
        leadInput.value = leadCheckboxTarget.value

        form.appendChild(leadInput)
      })

    } else {
      this.getFiltersParams().forEach(function(filterParam) {
        const formInput = document.createElement("input")
        formInput.type = "hidden"
        formInput.name = `campaign[leads_filters][${filterParam.name}]`
        formInput.value = filterParam.value

        form.appendChild(formInput)
      })
    }

    event.target.disabled = true
    event.target.innerHTML = `<div class='far fa-spinner-third fa-spin'></div> ${i18n.t("js.back.lead_actions.adding")}`

    form.submit()
  }

  // Appends one input per selected lead to the provided form.
  addSelectedLeadsToForm(form) {
    this.getSelectedLeads().forEach(function(leadCheckboxTarget) {
      const leadInput = document.createElement("input")
      leadInput.type = leadCheckboxTarget.type
      leadInput.name = leadCheckboxTarget.name
      leadInput.value = leadCheckboxTarget.value
      leadInput.checked = true

      form.appendChild(leadInput)
    })
  }

  // Appends one input per lead found in the view to the provided form.
  addFilteredLeadsToForm(form) {
    this.leadCheckboxTargets.forEach(function(leadCheckboxTarget) {
      const leadInput = document.createElement("input")
      leadInput.type = leadCheckboxTarget.type
      leadInput.name = leadCheckboxTarget.name
      leadInput.value = leadCheckboxTarget.value
      leadInput.checked = true

      form.appendChild(leadInput)
    })
  }

  addLeadsListIdToForm(form) {
    const leadsListInput = document.createElement("input")
    leadsListInput.type = "hidden"
    leadsListInput.name = "leads_list_id"
    leadsListInput.value = this.leadsListIdValue

    form.appendChild(leadsListInput)
  }

  generateForm(method, action) {
    const form = document.createElement("form")
    form.method = method
    form.action = action

    // CSRF protection disabled in test environment
    if (gon.environment !== "test") {
      const csrfTokenInput = document.createElement("input")
      csrfTokenInput.type = "hidden"
      csrfTokenInput.name = "authenticity_token"
      csrfTokenInput.value = document.head.querySelector("[name='csrf-token'][content]").content

      form.appendChild(csrfTokenInput)
    }

    if (method !== "post") {
      form.method = "post"

      const methodInput = document.createElement("input")
      methodInput.type = "hidden"
      methodInput.name = "_method"
      methodInput.value = method

      form.appendChild(methodInput)
    }

    document.body.appendChild(form)

    return form
  }

  getSelectedLeads() {
    return this.leadCheckboxTargets.filter(target => target.checked === true)
  }

  getFiltersParams() {
    let queryParams = location.search.substr(1).split("&")
    var filters = []

    queryParams.forEach(function(queryParam) {
      let name = queryParam.split("=")[0]
      let value = queryParam.split("=")[1]

      if (name !== "" && name !== "utf8" && value !== "" && value !== undefined) {
        filters.push({ name: name, value: value })
      }
    })

    return filters
  }

  displayGoogleConnectionRequiredFlash() {
    App.displayFlash("danger", i18n.t("js.back.lead_actions.connect_google_account_html"))
  }
}
